import React, { useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, Paper, Select, MenuItem, FormControl, InputLabel, Avatar } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { QuizContent, Question, QuestionType } from '../../../types/course';

// Editors
import QCMEditor from './quiz/QCMEditor';
import TrueFalseEditor from './quiz/TrueFalseEditor';
import FillBlankEditor from './quiz/FillBlankEditor';
import CodeEditor from './quiz/CodeEditor';
import MatchingEditor from './quiz/MatchingEditor';
import OrderEditor from './quiz/OrderEditor';

interface QuizBuilderProps {
    content: QuizContent;
    onUpdate: (content: QuizContent) => void;
}

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
    { value: 'qcm', label: 'QCM (Choix multiple)' },
    { value: 'true_false', label: 'Vrai / Faux' },
    { value: 'fill_blank', label: 'Texte à trous' },
    { value: 'code', label: 'Code à compléter' },
    { value: 'match_text', label: 'Appariement' },
    { value: 'order', label: 'Réordonnancement' },
];

const QuizBuilder: React.FC<QuizBuilderProps> = ({ content, onUpdate }) => {
    const questions = content.questions || [];

    // Auto-calculate points when questions change
    useEffect(() => {
        if (questions.length > 0) {
            const pointsPerQuestion = Math.floor(100 / questions.length);
            const remainder = 100 % questions.length;

            const updatedQuestions = questions.map((q, index) => ({
                ...q,
                points: pointsPerQuestion + (index < remainder ? 1 : 0)
            }));

            // Only update if points have actually changed to avoid infinite loop
            if (JSON.stringify(updatedQuestions) !== JSON.stringify(questions)) {
                onUpdate({ ...content, questions: updatedQuestions });
            }
        }
    }, [questions.length]); // Only re-calc when count changes

    const handleAddQuestion = () => {
        const newQuestion: Question = {
            type: 'qcm',
            questionText: '',
            points: 0,
            options: ['', ''],
            correctOptions: [],
            correctOptionIndex: 0,
            multiSelect: false
        };
        onUpdate({ ...content, questions: [...questions, newQuestion] });
    };

    const handleRemoveQuestion = (index: number) => {
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        onUpdate({ ...content, questions: newQuestions });
    };

    const handleQuestionChange = (index: number, updatedQuestion: Question) => {
        const newQuestions = [...questions];
        newQuestions[index] = updatedQuestion;
        onUpdate({ ...content, questions: newQuestions });
    };

    const renderEditor = (question: Question, index: number) => {
        const onChange = (updated: Question) => handleQuestionChange(index, updated);

        switch (question.type) {
            case 'qcm': return <QCMEditor question={question} onChange={onChange} />;
            case 'true_false': return <TrueFalseEditor question={question} onChange={onChange} />;
            case 'fill_blank': return <FillBlankEditor question={question} onChange={onChange} />;
            case 'code': return <CodeEditor question={question} onChange={onChange} />;
            case 'match_text': return <MatchingEditor question={question} onChange={onChange} />;
            case 'order': return <OrderEditor question={question} onChange={onChange} />;
            default: return <Typography color="error">Type de question inconnu</Typography>;
        }
    };

    return (
        <Box>
            <Box sx={{
                mb: 4,
                p: 2.5,
                bgcolor: 'rgba(16, 185, 129, 0.05)',
                borderRadius: 3,
                border: '1px solid rgba(16, 185, 129, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                backdropFilter: 'blur(10px)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HelpOutlineIcon sx={{ color: '#10b981' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#065f46', fontFamily: 'Quicksand, sans-serif' }}>
                        Questions: <strong>{questions.length}</strong>
                    </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#065f46', fontFamily: 'Quicksand, sans-serif' }}>
                    Total: <strong>100 points</strong>
                </Typography>
            </Box>

            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1, mb: 3 }}>
                {questions.map((q, index) => (
                    <Paper
                        key={index}
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 4,
                            border: '1px solid rgba(0,0,0,0.05)',
                            position: 'relative',
                            bgcolor: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 8px 30px rgba(0,0,0,0.03)',
                                borderColor: 'rgba(16, 185, 129, 0.2)'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 800, width: 32, height: 32, fontSize: '0.9rem' }}>
                                    {index + 1}
                                </Avatar>
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <Select
                                        value={q.type || 'qcm'}
                                        onChange={(e) => handleQuestionChange(index, { ...q, type: e.target.value as QuestionType })}
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            fontFamily: 'Quicksand, sans-serif',
                                            bgcolor: '#f8fafc'
                                        }}
                                    >
                                        {QUESTION_TYPES.map(type => (
                                            <MenuItem key={type.value} value={type.value} sx={{ fontWeight: 600 }}>{type.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ bgcolor: 'rgba(16, 185, 129, 0.05)', px: 1.5, py: 0.5, borderRadius: 2, border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#10b981' }}>
                                        {q.points} pts
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton size="small" onClick={() => handleRemoveQuestion(index)} sx={{ color: '#f56565', '&:hover': { bgcolor: 'rgba(245, 101, 101, 0.1)' } }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <TextField
                            fullWidth
                            placeholder="Saisissez votre question ici..."
                            value={q.questionText}
                            onChange={(e) => handleQuestionChange(index, { ...q, questionText: e.target.value })}
                            sx={{ mb: 3 }}
                            variant="outlined"
                            InputProps={{
                                sx: { fontWeight: 600, borderRadius: 2, bgcolor: '#f8fafc' }
                            }}
                        />

                        <Box sx={{ mb: 4, pl: 3, borderLeft: '3px solid rgba(16, 185, 129, 0.2)' }}>
                            {renderEditor(q, index)}
                        </Box>

                        <TextField
                            fullWidth
                            label="Explication (S'affiche après la réponse)"
                            value={q.explanation || ''}
                            onChange={(e) => handleQuestionChange(index, { ...q, explanation: e.target.value })}
                            multiline
                            rows={2}
                            variant="filled"
                            size="small"
                            InputProps={{
                                disableUnderline: true,
                                sx: { borderRadius: 2, fontSize: '0.85rem' }
                            }}
                        />
                    </Paper>
                ))}
            </Box>

            <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddQuestion}
                fullWidth
                sx={{
                    py: 2,
                    borderRadius: 3,
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    color: '#10b981',
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                    fontWeight: 800,
                    textTransform: 'none',
                    fontFamily: 'Quicksand, sans-serif',
                    fontSize: '1rem',
                    '&:hover': {
                        borderWidth: '2px',
                        borderColor: '#10b981',
                        bgcolor: 'rgba(16, 185, 129, 0.05)',
                        transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s'
                }}
            >
                Ajouter une nouvelle question
            </Button>
        </Box>
    );
};

export default QuizBuilder;
